import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { nip04, nip44 } from 'nostr-tools';
import { useNostr } from '@/hooks/useNostr';
import { useCurrentUser } from '@/hooks/useCurrentUser';

export interface NIP17Message {
  id: string;
  senderPubkey: string;
  recipientPubkey: string;
  content: string;
  timestamp: Date;
  replyTo?: string;
  status: 'sending' | 'sent' | 'delivered' | 'read';
}

/**
 * Hook for sending NIP-17 encrypted private messages
 */
export function useSendPrivMsg() {
  const { user } = useCurrentUser();

  return useMutation({
    mutationFn: async ({ recipientPubkey, content, replyTo }: {
      recipientPubkey: string;
      content: string;
      replyTo?: string;
    }) => {
      if (!user?.signer) throw new Error('User not authenticated');

      // Create the direct message event (kind: 14)
      const msgEvent = {
        kind: 14,
        content,
        created_at: Math.floor(Date.now() / 1000),
        tags: [
          ['p', recipientPubkey],
          ...(replyTo ? [['e', replyTo]] : [])
        ]
      };

      // Encrypt using NIP-44
      const encryptedContent = await user.signer.nip44.encrypt(
        recipientPubkey,
        JSON.stringify(msgEvent)
      );

      // Create the seal (kind: 13)
      const sealEvent = {
        kind: 13,
        content: encryptedContent,
        created_at: msgEvent.created_at,
        tags: []
      };

      // Sign the seal
      const signedSeal = await user.signer.signEvent(sealEvent);

      // Encrypt seal for gift wrap (kind: 1059)
      const giftWrapContent = await user.signer.nip44.encrypt(
        recipientPubkey,
        JSON.stringify(signedSeal)
      );

      // Create gift wrap event
      const giftWrapEvent = {
        kind: 1059,
        content: giftWrapContent,
        created_at: signedSeal.created_at + Math.floor(Math.random() * 172800), // Add up to 2 days
        tags: [['p', recipientPubkey]]
      };

      const randomKey = {}; // Generate random key for gift wrap
      giftWrapEvent.tags[0][2] = 'wss://relay.damus.io'; // Add relay hint

      return giftWrapEvent;
    }
  });
}

/**
 * Hook for receiving NIP-17 decrypted private messages
 */
export function usePrivMsgs(contactPubkey: string) {
  const { user } = useCurrentUser();
  const { nostr } = useNostr();

  return useQuery({
    queryKey: ['nip17-messages', contactPubkey],
    queryFn: async () => {
      if (!user?.signer) return [];

      // TODO: Query NIP-1059 (Gift Wrap) events from user's inbox relays
      // Decrypt gift wraps, then decrypt seals, then parse messages

      // For now, return mock data structure
      const mockMessages = [
        {
          id: 'msg1',
          senderPubkey: 'alice-pubkey',
          recipientPubkey: user.pubkey,
          content: '¡Hola! ¿Cómo estás?',
          timestamp: new Date(Date.now() - 1000 * 60 * 60 * 2),
          status: 'read' as const,
        },
        {
          id: 'msg2',
          senderPubkey: user.pubkey,
          recipientPubkey: 'alice-pubkey',
          content: '¡Hola Alice! Muy bien, gracias.',
          timestamp: new Date(Date.now() - 1000 * 60 * 45),
          status: 'sent' as const,
        },
      ];

      return mockMessages;
    },
    enabled: !!contactPubkey && !!user,
  });
}

/**
 * Hook for finding users by nickname (kind: 0 profiles)
 */
export function useFindUser(nickname: string) {
  const { nostr } = useNostr();

  return useQuery({
    queryKey: ['find-user', nickname],
    queryFn: async () => {
      if (!nickname.trim()) return null;

      try {
        // Query all kind:0 events (user profiles)
        const events = await nostr.query([
          {
            kinds: [0],
            limit: 100 // Get many profiles to find the right one
          }
        ], { signal: AbortSignal.timeout(5000) });

        // Find the user with matching nickname
        for (const event of events) {
          try {
            const profile = JSON.parse(event.content);
            // Check both name and display_name fields
            if (profile.name === nickname.trim() ||
                profile.display_name === nickname.trim()) {
              return {
                pubkey: event.pubkey,
                profile,
                relays: event.tags
                  .filter(tag => tag[0] === 'relay')
                  .map(tag => tag[1]),
                lastActivity: new Date(event.created_at * 1000)
              };
            }
          } catch (e) {
            // Skip invalid JSON
            continue;
          }
        }

        return null;
      } catch (error) {
        console.error('Error finding user:', error);
        return null;
      }
    },
    enabled: !!nickname.trim(),
    staleTime: 5 * 60 * 1000, // Cache for 5 minutes
  });
}

/**
 * Hook for managing user's nickname unique identifier
 */
export function useUserNicks() {
  const { user } = useCurrentUser();

  return useQuery({
    queryKey: ['user-nicks', user?.pubkey],
    queryFn: async () => {
      if (!user) return null;

      // Query the user's profile to get their nickname
      // In future implementations, this could include verified nicknames
      try {
        const profileEvents = await fetch(user.pubkey) // Mock for now
          .catch(() => null);

        return {
          nickname: user.profile?.name || user.profile?.display_name,
          verified: false, // In future, check NIP-05 verification
          pubkey: user.pubkey
        };
      } catch (error) {
        return null;
      }
    },
    enabled: !!user,
    staleTime: 10 * 60 * 1000, // Cache for 10 minutes
  });
}